/* eslint-disable-next-line react/prop-types */
import * as React from "react";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import axios from "axios";
import { DateTime } from "luxon";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import { toast } from "react-toastify";
import { app } from "@microsoft/teams-js";
import type { DateClickArg } from "@fullcalendar/interaction";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";

import {
  Dialog, Button, Flex, Input, Text, Divider, List,
  Label,
  Checkbox,
  Form,
  TextArea,
  Popup,
  Provider,
  teamsTheme
  , Dropdown
} from "@fluentui/react-northstar";
export const roomColors: { [key: string]: string } = {
  "Califoria@seta-international.vn": "#FF5733",
  "NewYork@seta-international.vn": "#33FF57",
  "Hanoi@seta-international.vn": "#3357FF",
  "Startup@seta-international.vn": "#F4C542"
};

const hexToRgba = (hex: string, alpha: number) => {
  const h = hex.replace("#", "");
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// RoomScheduleFullCalendar.tsx

export const convertScheduleToEvents = (schedule: any[]) => {
  return schedule.map(item => {
    // parse start
    let startDateTime: Date;
    if (typeof item.start === "string" && item.start.includes("T")) {
      // ISO string → parse trực tiếp
      startDateTime = DateTime.fromISO(item.start).toJSDate();
    } else {
      // fallback: `${day} ${HH:mm}`
      startDateTime = DateTime.fromFormat(
        `${item.day} ${item.start}`,
        "cccc, dd/MM/yyyy HH:mm"
      ).toJSDate();
    }

    // parse end (tương tự)
    let endDateTime: Date;
    if (typeof item.end === "string" && item.end.includes("T")) {
      endDateTime = DateTime.fromISO(item.end).toJSDate();
    } else {
      endDateTime = DateTime.fromFormat(
        `${item.day} ${item.end}`,
        "cccc, dd/MM/yyyy HH:mm"
      ).toJSDate();
    }

    const roomColor = roomColors[item.room] || "#00BFFF";
    const pastelColor = hexToRgba(roomColor, 0.3);

    return {
      id: item.id,
      title: item.subject || item.room.split("@")[0],
      resourceId: item.room,
      start: startDateTime,
      end: endDateTime,
      room: item.room,
      allDay: item?.isAllDay || false,
      backgroundColor: pastelColor,
      borderColor: roomColor,
      extendedProps: {
        userSeriesMasterId: item.userSeriesMasterId,
        organizer: item.organizer,
        participants: item.attendees,
        recurrence: item.isRecurring || null,
        title: item.subject || item.room.split("@")[0],
        seriesMasterId: item.seriesMasterId
      }
    };
  });
};

export default function RoomScheduleFullCalendar() {
  const initializedRef = useRef(false);
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [schedule, setSchedule] = useState<any[]>([]);
  const [offsetWeek, setOffsetWeek] = useState<number>(0);
  interface Room { emailAddress: string; displayName: string; }
  const [allRooms, setAllRooms] = useState<Room[]>([]);
  const [isRefetch, setIsRefetch] = useState<number>(0);
  const [isCreate, setIsCreate] = useState<boolean>(false);
  const [calendarKey, setCalendarKey] = useState(0);
  const [attendees, setAttendees] = useState<string[]>([]);
  const calendarRef = useRef<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const scheduleCacheRef = useRef<Record<string, any[]>>({});
  const [showMyEvents, setShowMyEvents] = useState(false);
  // State for booking form
  const [openBookingForm, setOpenBookingForm] = useState(false);
  const [formData, setFormData] = useState<{ start: Date; end: Date; room: string; subject: string }>({
    start: new Date(),
    end: new Date(),
    room: "",
    subject: ""
  });
  const [editMode, setEditMode] = useState<"single" | "series">("single");
  const [bookingRoom, setBookingRoom] = useState<string>("");
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [openEventDetails, setOpenEventDetails] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [userSuggestions, setUserSuggestions] = useState<{ displayName: string; mail: string }[]>([]);
  const [viewMode, setViewMode] = useState<"day" | "week" | "month">("week");
  const [offsetMonth, setOffsetMonth] = useState<number>(0);
  const [allDay, setAllDay] = useState(false);
  const [directEvent, setDirectEvent] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const selectedRoomsRef = useRef<string[]>([]);
  const prevSelectedRef = useRef<string[]>([]);
  const [bookingRoomName, setBookingRoomName] = useState<string>("");
  const [recurrence, setRecurrence] = useState<{
    frequency: "daily" | "weekly" | "monthly";
    interval: number;
    daysOfWeek: string[]; // ["monday","wednesday",...]
    endType: "noEnd" | "endDate";
    endDate: string | null;
  }>({
    frequency: "weekly",
    interval: 1,
    daysOfWeek: [],
    endType: "endDate",
    endDate: null
  });
  const dummyEventIdRef = useRef<string | null>(null);
  useEffect(() => {
    setCalendarKey(k => k + 1);
  }, [showMyEvents]);
  const displaySchedule = useMemo(() => {
    if (!showMyEvents) return schedule;
    return schedule.filter(item =>
      item.organizer === userEmail ||
      (item.attendees as string[]).includes(userEmail!)
    );
  }, [schedule, showMyEvents, userEmail]);

  const removeAttendee = (email: string) =>
    setAttendees(prev => prev.filter(a => a !== email));

  const handleEventDrop = async (info: any) => {
    // Giữ snapshot phòng khi rollback
    const previous = [...schedule];

    // Tạo updated props từ FullCalendar event
    const { id, start, end, extendedProps } = info.event;
    const { title: subject, organizer, participants } = extendedProps;

    // Chỉ cho phép organizer của mình move
    if (organizer !== userEmail) {
      toast.error("You can only move your own events.");
      info.revert(); // hoàn tác drag ngay lập tức
      return;
    }

    // Tính day/start/end dưới dạng string giống schedule item
    const startVN = DateTime.fromJSDate(start!).setZone("Asia/Ho_Chi_Minh");
    const endVN = DateTime.fromJSDate(end!).setZone("Asia/Ho_Chi_Minh");

    const updatedItem = {
      id,
      subject,
      room: info.event.extendedProps.room, // nhớ gắn room lên extendedProps khi convert lần trước
      organizer,
      attendees: participants,
      day: startVN.toFormat("cccc, dd/MM/yyyy"),
      start: startVN.toFormat("HH:mm"),
      end: endVN.toFormat("HH:mm")
    };

    // ❶ Cập nhật ngay UI
    setSchedule(s =>
      s.map(item => item.id === id ? updatedItem : item)
    );

    try {
      // ❷ Gọi API patch
      const res = await axios.patch(`/api/meeting-room-booking/${id}`, {
        organizerEmail: organizer,
        subject,
        start: start.toISOString(),
        end: end.toISOString(),
        attendees: participants,
        room: updatedItem.room
      }
      );
      if (res.status !== 200) throw new Error();
      setTimeout(() => setIsRefetch(prev => prev + 1), 4000);
      toast.success("Event moved!");
    } catch (err) {
      // ❸ Rollback nếu error
      console.error(err);
      toast.error("Move failed, reverting.");
      setSchedule(previous);
      info.revert && info.revert(); // hoàn tác calendar UI
    }
  };

  const handleUpdateEvent = async (eventId: string) => {
    // build updatedEvent từ formData / selectedEvent
    const updatedEvent = {
      id: eventId,
      subject: formData.subject || selectedEvent.title,
      room: bookingRoom || selectedEvent.room,
      organizer: userEmail!,
      attendees,
      // đây là JS Date
      start: formData.start || selectedEvent.start,
      end: formData.end || selectedEvent.end
    };

    if (!updatedEvent.room) {
      toast.error("Please fill your room");
      return;
    }

    try {
      const occurrences = [{ start: updatedEvent.start, end: updatedEvent.end }];
      for (const occ of occurrences) {
        const startIso = occ.start.toISOString();
        const endIso = occ.end.toISOString();
        const dtStart = new Date(startIso);
        dtStart.setMinutes(dtStart.getMinutes() + 1);
        const dtEnd = new Date(endIso);
        dtEnd.setMinutes(dtEnd.getMinutes() - 1);
        const newStartIso = dtStart.toISOString();
        const newEndIso = dtEnd.toISOString();
        const originalStartMs = DateTime.fromJSDate(selectedEvent.start, { zone: "Asia/Ho_Chi_Minh" })
          .toUTC()
          .toMillis();
        const originalEndMs = DateTime.fromJSDate(selectedEvent.end, { zone: "Asia/Ho_Chi_Minh" })
          .toUTC()
          .toMillis();
        const { data } = await axios.post("/api/available-rooms", {
          rooms: [updatedEvent.room],
          startTime: newStartIso,
          endTime: newEndIso
        });
        console.log(data);
        const conflict = data.some((sched: any) =>
          sched.scheduleItems.some((item: any) => {
            const slotStartMs = DateTime.fromISO(item.start.dateTime, {
              zone: item.start.timeZone
            }).toUTC().toMillis();
            const slotEndMs = DateTime.fromISO(item.end.dateTime, {
              zone: item.end.timeZone
            }).toUTC().toMillis();
            console.log(originalStartMs, slotStartMs);
            // nếu đúng slot của event gốc thì skip
            if (slotStartMs === originalStartMs && slotEndMs === originalEndMs) {
              return false;
            }
            return item.status.toLowerCase() !== "free";
          }
          )
        );
        if (conflict) {
          const fmtStart = DateTime.fromJSDate(occ.start)
            .setZone("Asia/Ho_Chi_Minh")
            .toFormat("dd/MM/yyyy HH:mm");
          const fmtEnd = DateTime.fromJSDate(occ.end)
            .setZone("Asia/Ho_Chi_Minh")
            .toFormat("HH:mm");
          toast.error("conflict event");
          return;
        }
      }
      const res = await axios.patch(
        `/api/meeting-room-booking/${eventId}`,
        {
          organizerEmail: updatedEvent.organizer,
          subject: updatedEvent.subject,
          start: updatedEvent.start.toISOString(),
          end: updatedEvent.end.toISOString(),
          attendees: updatedEvent.attendees,
          room: updatedEvent.room
        }
      );

      if (res.status === 200) {
        toast.success("Event updated successfully!");

        // --- Thay đổi ở đây: cập nhật trực tiếp lên schedule state ---
        setSchedule(prev =>
          prev.map(item => {
            if (item.id !== eventId) return item;

            // convert ngày giờ về string như schedule items
            const startVN = DateTime.fromJSDate(updatedEvent.start)
              .setZone("Asia/Ho_Chi_Minh");
            const endVN = DateTime.fromJSDate(updatedEvent.end)
              .setZone("Asia/Ho_Chi_Minh");

            return {
              ...item,
              subject: updatedEvent.subject,
              room: updatedEvent.room,
              organizer: updatedEvent.organizer,
              attendees: updatedEvent.attendees,
              day: startVN.toFormat("cccc, dd/MM/yyyy"),
              start: startVN.toFormat("HH:mm"),
              end: endVN.toFormat("HH:mm")
            };
          })
        );

        // đóng modal / reset form nếu cần
        setOpenBookingForm(false);
        setOpenEventDetails(false);
        setSelectedEvent(null);
        setFormData({ start: new Date(), end: new Date(), room: "", subject: "" });
        setAttendees([]);
        setTimeout(() => setIsRefetch(prev => prev + 1), 4000);
      } else {
        throw new Error();
      }
    } catch (error) {
      console.error("Error updating event:", error);
      toast.error("An error occurred while updating the event.");
    }
  };
  const getUsers = async (searchQuery: string) => {
    try {
      const response = await axios.get("/api/search-users", {
        params: { query: searchQuery }
      });
      setUserSuggestions(response.data); // Cập nhật gợi ý người dùng
    } catch (error) {
      console.error("Error fetching users from API:", error);
    }
  };

  // Khi người dùng thay đổi giá trị trong ô tìm kiếm
  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.currentTarget.value;
    setSearchQuery(query);

    if (query.length >= 3) { // Khi người dùng nhập ít nhất 3 ký tự
      await getUsers(query); // Gọi API để lấy người dùng
    } else {
      setUserSuggestions([]); // Nếu nhập ít hơn 3 ký tự, không hiển thị gợi ý
    }
  };

  // Thêm người vào danh sách attendees
  const handleAddAttendee = (email: string) => {
    setAttendees((prevAttendees) => [...prevAttendees, email]);
    setSearchQuery("");
    setUserSuggestions([]);
  };

  useEffect(() => {
    // Initialize the Teams SDK context
    app.initialize().then(() => {
      app.getContext().then((context) => {
        const email = context?.user?.userPrincipalName;
        setUserEmail(email || null);
      }).catch((error) => {
        console.error("Failed to get context:", error);
      });
    });
  }, []);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Create a function to handle changing the selected week
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = new Date(e.currentTarget.value);
    const currentStartOfWeek = DateTime.now().startOf("week");
    const newStartOfWeek = DateTime.fromJSDate(selected).startOf("week");

    // Tính sự chênh lệch giữa tuần hiện tại và tuần đã chọn
    const diffInWeeks = newStartOfWeek.diff(currentStartOfWeek, "weeks").weeks;

    // Cập nhật lại offset
    setOffsetWeek(Math.round(diffInWeeks));

    // Cập nhật lại giá trị ngày đã chọn
    setSelectedDate(selected);
    if (calendarRef.current) {
      calendarRef.current.getApi().gotoDate(newStartOfWeek.toJSDate()); // Goto the new week
    }

  };

  const fetchSchedule = useCallback(
    async (roomsParam: string[]) => {
      const params: any = { rooms: roomsParam, mode: viewMode };
      if (viewMode === "day") params.date = DateTime.fromJSDate(selectedDate).toISODate();
      if (viewMode === "week") params.offsetWeek = offsetWeek;
      if (viewMode === "month") params.offsetMonth = offsetMonth;

      try {
        const res = await axios.get("/api/room-schedule", { params });
        const nowSelected = selectedRoomsRef.current;
        setSchedule(
          res.data.filter((ev: any) => nowSelected.includes(ev.room))
        );
      } catch (err) {
        console.error(err);
      }
    },
    [viewMode, selectedDate, offsetWeek, offsetMonth] // không có selectedRooms
  );

  // khi Calendar render xong hoặc đổi view/week/day/month
  const handleDatesSet = (arg: any) => {
    // xác định mode từ view type
    const viewType = arg.view.type;
    if (viewType === "timeGridWeek") {
      setViewMode("week");
      // tính offsetWeek dựa vào arg.start
      const currentStart = DateTime.now().startOf("week");
      const newStart = DateTime.fromJSDate(arg.start).startOf("week");
      setOffsetWeek(Math.round(newStart.diff(currentStart, "weeks").weeks));
    } else if (viewType === "timeGridDay") {
      setViewMode("day");
      setSelectedDate(arg.start);
    } else if (viewType === "dayGridMonth") {
      setViewMode("month");
      // tính offsetMonth dựa vào arg.start
      const currentStart = DateTime.now().startOf("month");
      const newMonthStart = DateTime.fromJSDate(arg.start).startOf("month");
      setOffsetMonth(Math.round(newMonthStart.diff(currentStart, "months").months) + 1);
    }
  };

  const fetchRooms = async () => {
    const res = await axios.get("/api/rooms");
    const emails = res.data.map((r: any) => r.emailAddress);
    const name = res.data.map((r: any) => r.displayName);
    setBookingRoomName(name);
    setAllRooms(res.data);
  };
  useEffect(() => {
    if (initializedRef.current || allRooms.length === 0) return;

    initializedRef.current = true;
    const emails = allRooms.map(room => room.emailAddress);
    setSelectedRooms(emails);
    selectedRoomsRef.current = emails;
    fetchSchedule(emails);
  }, [allRooms, fetchSchedule]);

  useEffect(() => {
    fetchRooms();
  }, []);

  // useEffect(() => {
  //     // Clear the cache only when selectedRooms changes
  //     fetchedWeeksRef.current = {};
  //     fetchSchedule(); // Refresh schedule
  // }, [selectedRooms, fetchSchedule]); // Do not include offsetWeek here

  // Keep fetching the schedule whenever offsetWeek or selectedRooms changes
  useEffect(() => {
    selectedRoomsRef.current = selectedRooms;
  }, [selectedRooms]);

  const fetchOnlyNewRoom = useCallback(async (room: string) => {
    const key = `${offsetWeek}-${room}`;

    // 1) Nếu đã cache thì đổ data từ cache ngay và return
    if (scheduleCacheRef.current[key]) {
      setSchedule(prev => [...prev, ...scheduleCacheRef.current[key]!]);
      return;
    }

    // 2) Chưa có cache -> gọi API, lọc, lưu cache rồi cập nhật UI
    try {
      const res = await axios.get("/api/room-schedule", {
        params: {
          rooms: [room],
          mode: viewMode,
          offsetWeek,
          offsetMonth,
          date: viewMode === "day"
            ? DateTime.fromJSDate(selectedDate).toISODate()
            : undefined
        }
      });
      const nowSel = selectedRoomsRef.current;
      const filtered = res.data.filter((ev: any) => nowSel.includes(ev.room));

      // lưu vào cache
      scheduleCacheRef.current[key] = filtered;
      // cập nhật UI
      setSchedule(prev => [...prev, ...filtered]);
    } catch (err) {
      console.error("Error fetching room:", err);
    }
  }, [offsetWeek, viewMode, offsetMonth, selectedDate]);

  const handleRoomSelection = async (room: string) => {
    setSelectedRooms((prevRooms) => {
      const isAlready = prevRooms.includes(room);

      if (isAlready) {
        // — BỎ phòng: chỉ filter local schedule
        setSchedule(prev =>
          prev.filter(item => item.room !== room)
        );
        return prevRooms.filter(r => r !== room);
      } else {
        // — THÊM phòng: append vào selectedRooms và chỉ fetch room mới
        const next = [...prevRooms, room];
        fetchOnlyNewRoom(room);
        return next;
      }
    });
  };
  useEffect(() => {
    // prevSelectedRef để tracking thay đổi
    const prev = prevSelectedRef.current;
    const curr = selectedRooms;
    // rooms mới bật lên
    const added = curr.filter(r => !prev.includes(r));
    // rooms vừa tắt đi
    const removed = prev.filter(r => !curr.includes(r));

    // 1) Xoá event của rooms vừa tắt
    if (removed.length) {
      setSchedule(s => s.filter(ev => !removed.includes(ev.room)));
    }

    // 2) Với rooms mới bật: đổ data (từ cache hoặc fetch mới)
    added.forEach(room => fetchOnlyNewRoom(room));

    // cập nhật prevSelected
    prevSelectedRef.current = curr;
  }, [selectedRooms, fetchOnlyNewRoom]);
  const handleCreateBooking = async (start: Date, end: Date) => {
    console.log(start, end);
    if (!formData.subject) {
      toast.error("Please fill your subject");
      return;
    };
    if (!bookingRoom) {
      toast.error("Please fill your room");
      return;
    };
    const generateOccurrences = (): { start: Date; end: Date }[] => {
      const dates: { start: Date; end: Date }[] = [];
      const { frequency, interval, daysOfWeek, endDate: recEnd } = recurrence;

      // Nếu chưa có endDate thì trả về rỗng
      if (!recEnd) return dates;

      // 1) Parse start và endDate
      const startDT = DateTime.fromJSDate(start)
        .setZone("Asia/Ho_Chi_Minh");
      const rawEnd = typeof recEnd === "string"
        ? DateTime.fromISO(recEnd)
        : DateTime.fromJSDate(recEnd);

      // 2) Align finalDate về cùng time-of-day với start
      const finalDate = rawEnd
        .setZone("Asia/Ho_Chi_Minh")
        .set({
          hour: startDT.hour,
          minute: startDT.minute,
          second: startDT.second,
          millisecond: startDT.millisecond
        });

      // 3) Hàm thêm occurrence vào mảng
      const addOccurrence = (dt: DateTime) => {
        dates.push({
          start: dt.toJSDate(),
          end: DateTime.fromJSDate(end)
            .setZone("Asia/Ho_Chi_Minh")
            .set({ year: dt.year, month: dt.month, day: dt.day })
            .toJSDate()
        });
      };

      // 4) Sinh theo frequency
      if (frequency === "daily") {
        let cursor = startDT;
        while (cursor <= finalDate) {
          addOccurrence(cursor);
          cursor = cursor.plus({ days: interval });
        }

      } else if (frequency === "weekly") {
        // map ["monday",...] → [1,2,...]
        const dowMap = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
        const dowNums = daysOfWeek.map(d => dowMap.indexOf(d) + 1);

        // Khởi tuần đầu
        let weekStart = startDT.startOf("week");
        // Duyệt tuần tới khi vẫn ≤ tuần chứa finalDate
        while (weekStart.startOf("week") <= finalDate.startOf("week")) {
          for (const wd of dowNums) {
            const dt = weekStart.set({ weekday: wd, hour: startDT.hour, minute: startDT.minute });
            if (dt >= startDT && dt <= finalDate) {
              addOccurrence(dt);
            }
          }
          weekStart = weekStart.plus({ weeks: interval });
        }

      } else if (frequency === "monthly") {
        let cursor = startDT;
        while (cursor <= finalDate) {
          addOccurrence(cursor);
          cursor = cursor.plus({ months: interval });
        }
      }

      return dates;
    };

    const occurrences: { start: Date; end: Date }[] = isRecurring
      ? generateOccurrences() // [ { start, end }, ... ]
      : [{ start, end }];

    // 2) Check availability cho từng occurrence
    try {
      for (const occ of occurrences) {
        console.log(occ);
        const iso = occ.start.toISOString(); // "2025-05-22T10:00:00.000Z"
        const iso1 = occ.end.toISOString();
        // 2️⃣ Parse thành Date, cộng 1 phút
        const dt = new Date(iso);
        dt.setMinutes(dt.getMinutes() + 1);
        const dt1 = new Date(iso1);
        dt1.setMinutes(dt1.getMinutes() - 1);
        // 3️⃣ Xuất chuỗi ISO mới
        const newIso = dt.toISOString();
        const newIso1 = dt1.toISOString();

        console.log(newIso);
        const { data } = await axios.post("/api/available-rooms", {
          rooms: [bookingRoom],
          startTime: newIso,
          endTime: newIso1
        });

        const conflict = data.some((sched: any) =>
          sched.scheduleItems.some((item: any) => item.availability !== "free")
        );
        if (conflict) {
          const fmtStart = DateTime.fromJSDate(occ.start)
            .setZone("Asia/Ho_Chi_Minh")
            .toFormat("dd/MM/yyyy HH:mm");
          const fmtEnd = DateTime.fromJSDate(occ.end)
            .setZone("Asia/Ho_Chi_Minh")
            .toFormat("HH:mm");
          toast.error("conflict event");
          return; // dừng không tạo
        }

      }
    } catch (err) {
      console.error("Error checking availability:", err);
      toast.error("Cannot check availability please try again");
      return;
    }

    const fakeIdBase = `temp-${Date.now()}`;
    dummyEventIdRef.current = fakeIdBase;

    // Tạo dummy events
    const dummyEvents = isRecurring
      ? generateOccurrences().map((occ, idx) => {
        const dayFmt = occ.start
          ? DateTime.fromJSDate(occ.start)
            .setZone("Asia/Ho_Chi_Minh")
            .toFormat("cccc, dd/MM/yyyy")
          : "";
        const startFmt = allDay
          ? "All day"
          : DateTime.fromJSDate(occ.start)
            .setZone("Asia/Ho_Chi_Minh")
            .toFormat("HH:mm");
        const endFmt = allDay
          ? ""
          : DateTime.fromJSDate(occ.end)
            .setZone("Asia/Ho_Chi_Minh")
            .toFormat("HH:mm");

        return {
          id: fakeIdBase,
          subject: formData.subject,
          room: bookingRoom,
          organizer: userEmail,
          attendees,
          allDay,
          day: dayFmt,
          start: startFmt,
          end: endFmt
        };
      })
      : [
        {
          id: fakeIdBase,
          subject: formData.subject,
          room: bookingRoom,
          organizer: userEmail,
          attendees,
          allDay,
          day: DateTime.fromJSDate(start)
            .setZone("Asia/Ho_Chi_Minh")
            .toFormat("cccc, dd/MM/yyyy"),
          start: allDay
            ? "All day"
            : DateTime.fromJSDate(start)
              .setZone("Asia/Ho_Chi_Minh")
              .toFormat("HH:mm"),
          end: allDay
            ? ""
            : DateTime.fromJSDate(end)
              .setZone("Asia/Ho_Chi_Minh")
              .toFormat("HH:mm")
        }
      ];

    // Optimistic UI: thêm tất cả dummy events
    setSchedule(prev => [...prev, ...dummyEvents]);

    // build payload như cũ...
    const payload: any = { organizerEmail: userEmail, roomEmail: bookingRoom, attendees, allDay, subject: formData.subject };
    if (allDay) {
      const s = DateTime.fromJSDate(start).setZone("UTC").startOf("day").toISO();
      const e = DateTime.fromJSDate(end).setZone("UTC").plus({ days: 1 }).startOf("day").toISO();
      payload.start = s; payload.end = e;
    } else {
      payload.start = start.toISOString(); payload.end = end.toISOString();
    }
    if (isRecurring) {
      payload.recurrence = {
        pattern: {
          type: recurrence.frequency.toUpperCase(),
          interval: recurrence.interval,
          ...(recurrence.frequency === "weekly" && { daysOfWeek: recurrence.daysOfWeek.map(d => d[0].toUpperCase() + d.slice(1)) })
        },
        range: {
          type: recurrence.endType === "noEnd" ? "noEnd" : "endDate",
          startDate: DateTime.fromJSDate(formData.start).toISODate(),
          ...(recurrence.endType === "endDate" && { endDate: recurrence.endDate })
        }
      };
    }

    try {
      const res = await axios.post("/api/meeting-room-booking", payload);
      if (res.status === 200) {
        toast.success("Done");
        setIsCreate(true);
        setTimeout(() => setIsRefetch(prev => prev + 1), 4000);
      } else {
        throw new Error();
      }
    } catch (error) {
      // rollback all dummy events
      setSchedule(prev => prev.filter(ev => !ev.id.startsWith(fakeIdBase)));
      toast.error("Fail to book meeting room");
    } finally {
      setOpenBookingForm(false);
      setFormData({ start: new Date(), end: new Date(), room: "", subject: "" });
      setAttendees([]);
    }
  };
  const handleDateClick = (clickInfo: DateClickArg) => {
    if (clickInfo.date < new Date()) {
      toast.error("Cannot book in the past");
      return;
    }
    const start = clickInfo.date;

    const end = new Date(start.getTime() + 60 * 30 * 1000);
    const defaultRoom = selectedRooms.length === 1
      ? selectedRooms[0]
      : "";
    console.log(defaultRoom);
    setBookingRoom(defaultRoom);

    // set vào state formData
    setFormData(prev => ({
      ...prev,
      start,
      room: defaultRoom,
      end

    }));
    setOpenBookingForm(true);
  };
  const handleUpdateSeries = async () => {
    if (!selectedEvent) return;

    try {
      // 1) Build recurrence payload từ state `recurrence` và formData.start/end
      const recurrencePayload = {
        pattern: {
          type: recurrence.frequency.toUpperCase(), // "DAILY", "WEEKLY", "MONTHLY"
          interval: recurrence.interval,
          ...(recurrence.frequency === "weekly" && {
            daysOfWeek: recurrence.daysOfWeek.map(
              d => d[0].toUpperCase() + d.slice(1) // ["monday"] → ["Monday"]
            )
          })
        },
        range: {
          type: recurrence.endType === "noEnd" ? "noEnd" : "endDate",
          startDate: DateTime.fromJSDate(formData.start).toISODate(),
          ...(recurrence.endType === "endDate" && { endDate: recurrence.endDate! })
        }
      };

      // 2) Gọi API PATCH lên master series
      const res = await axios.patch(
        `/api/meeting-room-booking/series/${selectedEvent.extendedProps.userSeriesMasterId}`,
        {
          organizerEmail: userEmail, // hoặc lấy từ context/auth
          subject: formData.subject,
          start: formData.start.toISOString(),
          end: formData.end.toISOString(),
          roomEmail: bookingRoom,
          attendees, // state array của bạn
          recurrence: recurrencePayload
        }
      );

      // 3) Xử lý kết quả
      if (res.status === 200) {
        toast.success("Cập nhật chuỗi thành công!");
        // rerender lại calendar
        setTimeout(() => setIsRefetch(prev => prev + 1), 4000);
        // đóng form và reset selection
        setOpenBookingForm(false);
        setSelectedEvent(null);
      } else {
        throw new Error(`Unexpected status ${res.status}`);
      }
    } catch (error: any) {
      console.error("Failed to update series:", error);
      toast.error("Cập nhật chuỗi thất bại");
    }
  };

  const handleEventClick = (info: any) => {
    const event = info.event;
    setSelectedEvent(event);
    setFormData(event);
    setOpenEventDetails(true);
  };
  const promptDelete = () => setShowDeleteConfirm(true);
  const handleDeleteEvent = async (deleteSeries: boolean) => {
    setShowDeleteConfirm(false);
    if (!selectedEvent) return;

    try {
      // Gọi duy nhất /api/meeting-room-booking/:id với body chứa deleteSeries & seriesMasterId
      const res = await axios.delete(
        `/api/meeting-room-booking/${selectedEvent.id}`,
        {
          data: {
            organizerEmail: userEmail,
            deleteSeries,
            seriesMasterId: selectedEvent.extendedProps.seriesMasterId || null // cần backend hỗ trợ
          }
        }
      );

      if (res.status === 200) {
        setOpenEventDetails(false);
        // Cập nhật lại state schedule
        if (deleteSeries) {
          // Xóa cả series: giữ lại những event không cùng seriesMasterId
          setSchedule(prev =>
            prev.filter(item =>
              item.seriesMasterId !== selectedEvent.extendedProps.seriesMasterId
            )
          );
        } else {
          // Xóa single occurrence
          setSchedule(prev =>
            prev.filter(item => item.id !== selectedEvent.id)
          );
        }
        setTimeout(() => setIsRefetch(prev => prev + 1), 4000);
        toast.success("Delete successful!");
      } else {
        throw new Error(`Status ${res.status}`);
      }
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Delete fail");
    }
  };
  useEffect(() => {
    if (allRooms.length) {
      const emails = allRooms.map(room => room.emailAddress);
      setSelectedRooms(emails);
    }
  }, [allRooms]);
  useEffect(() => {
    const scrollToNowIndicator = () => {
      const nowIndicator = document.querySelector(".fc-timegrid-now-indicator") as HTMLElement;
      const timegridBody = document.querySelector(".fc-timegrid-body") as HTMLElement;

      if (nowIndicator && timegridBody) {
        const offsetTop = nowIndicator.getBoundingClientRect().top -
          timegridBody.getBoundingClientRect().top;
        timegridBody.scrollTop = offsetTop - 40; // offset thêm chút để khỏi dính sát mép
      }
    };

    setTimeout(scrollToNowIndicator, 1000); // đợi FullCalendar render xong
  }, []);
  useEffect(() => {
    // 1) Lấy tạm data từ cache cho tuần mới
    const initial: any[] = [];
    selectedRoomsRef.current.forEach(room => {
      const key = `${offsetWeek}-${room}`;
      if (scheduleCacheRef.current[key]) {
        initial.push(...scheduleCacheRef.current[key]!);
      }
    });
    setSchedule(initial);

    // 2) Với những room chưa cache tuần này → fetch thêm
    selectedRoomsRef.current.forEach(room => {
      const key = `${offsetWeek}-${room}`;
      if (!scheduleCacheRef.current[key]) {
        fetchOnlyNewRoom(room);
      }
    });
  }, [offsetWeek, fetchOnlyNewRoom]);
  // ❷ Khi thực sự cần refetch toàn bộ (ví dụ sau tạo mới/chỉnh series)
  useEffect(() => {
    // bỏ chạy lần mount đầu (isRefetch thường khởi tạo = 0)
    if (isRefetch === 0) return;

    // 2) Xóa cache chỉ của tuần hiện tại
    Object.keys(scheduleCacheRef.current)
      .filter(k => k.startsWith(`${offsetWeek}-`))
      .forEach(k => delete scheduleCacheRef.current[k]);

    // 3) Fetch lại dữ liệu cho tất cả các room
    Promise.all(
      selectedRoomsRef.current.map(room => fetchOnlyNewRoom(room))
    )
      .then(() => {
        // 3) Chỉ sau khi fetch xong mới clear UI cũ (dummy event)
        if (isCreate) {
          setSchedule(prev =>
            prev.filter(ev => ev.id !== dummyEventIdRef.current)
          );
          setCalendarKey(k => k + 1);
        }
      })
      .catch(console.error);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefetch]);

  return (
    <>
      <style>{`
 /* Base Calendar Styling */
 .fc {
   background: #2e2e2e !important;
   color: #ccc;
 }

 /* 1. Toolbar Sticky */
 .fc-header-toolbar {
   position: sticky;
   top: 0;
   background: #2e2e2e !important;
   border-bottom: 1px solid #444;
   z-index: 50;
   padding: 0.5rem 0;
 }

 @media (min-width: 768px) {
  .fc .fc-col-header-cell,
  .fc .fc-scrollgrid-section-header th {
    background: #2e2e2e !important;
    border-bottom: 1px solid #444 !important;
    height: 4rem !important;
    padding: 0 !important;
  }
  .fc .fc-timegrid-all-day-cushion {
    border-bottom: 1px solid #444 !important;
  }
}

/* —— Mobile only: hide the separator —— */
@media (max-width: 767px) {
  .fc .fc-col-header-cell,
  .fc .fc-scrollgrid-section-header th {
    border-bottom: none !important;
  }
  .fc .fc-timegrid-all-day-cushion {
    border-bottom: none !important;
  }
}

 /* 2. Date Row Sticky */
 tr.fc-scrollgrid-section-header.fc-scrollgrid-section-sticky > th {
   background: #2e2e2e !important;
   position: sticky;
   top: 52px !important;
   z-index: 49;
   height: 4rem;
 }
 /* Center nội dung ngày */
.fc .fc-col-header-cell-cushion {
  display: flex !important;
  flex-direction: column-reverse !important;
  align-items: center !important;
  justify-content: center !important;
  height: 100% !important;
}

 .fc .fc-col-header-cell.fc-day-today {
   background-color: rgba(41, 121, 255, 0.2) !important;
 }

 /* 3. All-Day Slot Sticky */
 .fc .fc-scrollgrid-section-body:first-of-type {
   display: block;
 }
 .fc .fc-scrollgrid-section-body:first-of-type tbody > tr:first-child {
   position: sticky;
   top: calc(52px + 4rem);
   z-index: 48;
   background: #2e2e2e !important;

 }
 .fc .fc-scrollgrid-section-body:first-of-type tbody > tr:first-child td {
   background: #2e2e2e;

 }
 .fc .fc-scrollgrid-section-body:first-of-type tbody > tr:not(:first-child) {
   position: relative;
 }

 /* 4. Grid Styling */
 .fc .fc-scrollgrid thead th,
 .fc .fc-scrollgrid-section-body td {
   border-right: 1px solid #444 !important;
 }
 .fc .fc-scrollgrid thead th:last-child,
 .fc .fc-scrollgrid-section-body td:last-child {
   border-right: none !important;
 }
 .fc .fc-scrollgrid-section-body td {
   border-color: #444 !important;
 }

 /* 5. DayGrid rows fixed 4rem */
 .fc-daygrid-body .fc-scrollgrid-section-body tr {
   height: 4rem !important;
 }

 /* 6. TimeGrid slots fixed 4rem */
 .fc .fc-timegrid-slot {
   height: 4rem !important;
   min-height: 4rem !important;
   background: #2e2e2e;
 }
 .fc .fc-timegrid-axis-cushion {
   color: #888;
   font-size: 0.85rem;
 }

 /* 7. Ensure event boxes stretch */
 .fc-event {
   position: absolute !important;
   height: auto !important;
   border-radius: 4px;
   opacity: 0.95;
 }

 /* 8. Now Line */
 .fc .fc-timegrid-now-indicator {
   border-top: 2px solid #82b1ff !important;
   left: 0 !important;
   right: 0 !important;
   width: auto !important;
 }
 .fc .fc-timegrid-now-indicator-arrow {
   border-bottom-color: #82b1ff !important;
 }

 /* 9. Hover enhancements */
 .fc .fc-daygrid-day:hover,
 .fc .fc-timegrid-col:hover {
   background-color: rgba(255, 255, 255, 0.05) !important;
 }

 /* 10. All-day cushion styling */
 .fc .fc-timegrid-all-day-cushion {
   background: #2e2e2e !important;
   border-bottom: none !important; 
   border-right: 1px solid #444 !important;
   min-height: 4rem;
 }
  /* Grid Styling */
  .fc .fc-scrollgrid thead th,
  .fc .fc-scrollgrid-section-body td {
    border-right: 1px solid #444 !important;
  }

  .fc .fc-scrollgrid thead th:last-child,

  .fc .fc-scrollgrid-section-body td {
    border-color: #444 !important;
  }

  .fc .fc-scrollgrid-section-body tr {
    height: 4rem !important;
  }

  .fc .fc-timegrid-slot {
    min-height: 6rem;
    background: #2e2e2e;
  }

  .fc .fc-timegrid-axis-cushion {
    color: #888;
    font-size: 0.85rem;
  }
  .fc .fc-col-header-cell.fc-day-today {
    background-color: rgba(41, 121, 255, 0.2) !important;
  }
  
  /*  Bôi nền nhẹ cho tất cả ô time-slot của ngày hôm nay */
  .fc .fc-scrollgrid-section-body td.fc-day.fc-day-today {
    background-color: rgba(41, 121, 255, 0.05) !important;
  }
  
  /*  Khi hover ô time-slot hôm nay, nhấn màu đậm hơn một chút */
  .fc .fc-scrollgrid-section-body td.fc-day.fc-day-today:hover {
    background-color: rgba(41, 121, 255, 0.1) !important;
  }
  
  /*  (Nếu dùng all-day row) Đồng bộ all-day row hôm nay */
  .fc .fc-scrollgrid-section-body:first-of-type tbody > tr:first-child td.fc-day-today {
    background-color: rgba(41, 121, 255, 0.05) !important;
  }  


  /* Highlight Today */
  .fc .fc-col-header-cell.fc-day-today {
    background-color: rgba(25, 42, 95, 0.2) !important;
  }

  .fc .fc-timegrid-col.fc-day-today {
    background-color: rgba(25, 42, 95, 0.2) !important;
  }

  /* Now Line Styling */
  .fc .fc-timegrid-now-indicator {
    border-top: 2px solid #82b1ff !important;
    left: 0 !important;
    right: 0 !important;
    width: auto !important;
  }

  .fc .fc-timegrid-now-indicator-arrow {
    border-bottom-color: #82b1ff !important;
  }
  /* Event Style */
  .fc-event {
    border-radius: 4px;
    opacity: 0.95;
  }
  /* ——— Restore right borders on all grid cells ——— */
  .fc .fc-scrollgrid th,
  .fc .fc-scrollgrid td {
    border-right: 1px solid #444 !important;
  }
  
  /* ——— Remove right border on the last cell of each row ——— */
  .fc .fc-scrollgrid tr > *:last-child {
    border-right: none !important;
  }
  /* OVERLAY */
  .custom-modal-overlay {
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.6); z-index: 1000;
  }
  /* MODAL BOX */
  .custom-modal {
    position: fixed; top: 50%; left: 50%;
    border: 2px solid #2979ff;
    transform: translate(-50%, -50%);
    width: 550px; max-width: 95%;
    background: #1e1e1e; color: #fff;
    border-radius: 8px; padding: 1.5rem;
    box-shadow: 0 8px 16px rgba(0,0,0,0.5);
    z-index: 1001;
    display: flex; flex-direction: column;
  }
  .custom-modal-header {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 1rem;
  }
  .custom-modal-header h2 {
    margin: 0; font-size: 1.25rem; font-weight: 600;
  }
  .form-row { margin-bottom: 1rem; position: relative; }
  .custom-modal input[type="text"],
  .custom-modal input[type="date"],
  .custom-modal input[type="time"],
  .custom-modal textarea {
    width: 100%; background: #2e2e2e; color: #fff;
    border: 1px solid #444; border-radius: 4px;
    padding: 0.5rem; font-size: 0.9rem;
  }
  .custom-modal textarea { min-height: 120px; resize: vertical; }
  .suggestions-dropdown {
    position: absolute; top: calc(100% + 4px); left: 0; right: 0;
    max-height: 200px; overflow-y: auto;
    background: #2e2e2e; border: 1px solid #444; border-radius: 4px;
    z-index: 1002;
  }
  .suggestions-dropdown .suggestion-item {
    padding: 0.5rem; cursor: pointer; color: #ccc;
  }
  .suggestions-dropdown .suggestion-item:hover {
    background: rgba(255,255,255,0.1);
  }
  .attendee-tokens {
    display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.5rem;
  }
  .attendee-token {
    display: inline-flex; align-items: center;
    background: #2979ff; color: #fff; border-radius: 12px;
    padding: 0.25rem 0.5rem; font-size: 0.85rem;
  }
  .attendee-token .remove-token {
    margin-left: 0.5rem; cursor: pointer; font-weight: bold;
  }
  .toggle-wrapper {
    display: flex; align-items: center; gap: 0.5rem;
  }
  .toggle-wrapper input[type="checkbox"] {
    -webkit-appearance: none; width: 36px; height: 20px;
    background: #555; border-radius: 10px; cursor: pointer;
    position: relative; transition: background-color 0.2s;
  }
  .toggle-wrapper input[type="checkbox"]:checked {
    background: #2979ff;
  }
  .toggle-wrapper input[type="checkbox"]::before {
    content: ""; position: absolute;
    top: 2px; left: 2px; width: 16px; height: 16px;
    background: #fff; border-radius: 50%;
    transition: transform 0.2s;
  }
  .toggle-wrapper input[type="checkbox"]:checked::before {
    transform: translateX(16px);
  }
  .custom-modal-buttons {
    display: flex; justify-content: flex-end; gap: 1rem;
    margin-top: 1.5rem;
  }
  .btn-cancel, .btn-save {
    padding: 0.5rem 1rem; border: none; border-radius: 4px;
    cursor: pointer; font-size: 0.9rem;
  }
  .btn-cancel { background: #555; color: #fff; }
  .btn-save   { background: #2979ff; color: #fff; }
  .recurrence-toggle {
    background: transparent;
    border: none;
    color: #2979ff;
    font-weight: 600;
    cursor: pointer;
    padding: 0.5rem 0;
  }
  .recurrence-toggle .icon {
    margin-right: 0.25rem;
  }
  .recurrence-form .form-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }
  .weekdays-row .day {
    padding: 0.4rem 0.6rem;
    border-radius: 4px;
    background: #2e2e2e;
    color: #ccc;
    border: 1px solid #444;
    cursor: pointer;
  }
  .weekdays-row .day.active {
    background: #2979ff;
    color: #fff;
    border-color: #2979ff;
  }
  .left-sidebar,
.left-sidebar * {
  /* force every piece of text in that panel to be white */
  color: #fff !important;
}
input[type="date"] {
  color: #fff !important;
}

/* WebKit “parts” of the date input */
input[type="date"]::-webkit-datetime-edit,
input[type="date"]::-webkit-datetime-edit-fields-wrapper,
input[type="date"]::-webkit-datetime-edit-text,
input[type="date"]::-webkit-datetime-edit-month-field,
input[type="date"]::-webkit-datetime-edit-day-field,
input[type="date"]::-webkit-datetime-edit-year-field {
  color: #fff !important;
}

/* Flip the calendar icon to white if needed */
input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(1) !important;
}
input[type="date"] {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  /* 2) Force your dark background */
  background-color: #1e1e1e !important;

  /* 3) Keep the text white too */
  color: #fff !important;

  /* optional: tweak padding/border */
  border: 1px solid #444 !important;
  border-radius: 4px !important;
  padding: 0.5rem !important;
}

/* WebKit date parts can sometimes bleed through */
input[type="date"]::-webkit-datetime-edit,
input[type="date"]::-webkit-datetime-edit-fields-wrapper {
  background-color: #1e1e1e !important;
  color: #fff !important;
}

/* Make the calendar icon visible on dark bg */
input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(1) !important;
}
.fc .fc-scrollgrid-section-body:nth-of-type(2) td {
  border-right: 1px solid #444 !important;
}

.fc .fc-timegrid-body td {
  border-right: 1px solid #444 !important;
}

.fc .fc-timegrid-cols > table > tbody > tr > td {
  /* give every day-column (and the axis cell) a right border */
  border-right: 1px solid #444 !important;
}

.fc .fc-timegrid-col-frame {
  border-right: 1px solid #444 !important;
  box-sizing: border-box;
  background-clip: padding-box;
}
.fc .fc-timegrid-body td {
  border-right: 1px solid #444 !important;
}
.fc .fc-timegrid-slots tr > td {
  border-right: 1px solid #444 !important;
  box-sizing: border-box;
}
.fc .fc-timegrid-col {
  border-right: 1px solid #444 !important;
  box-sizing: border-box;
}
.fc .fc-timegrid-col:not(:last-child)::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 1px;
  height: 100%;
  background-color: #444;
  pointer-events: none;
}
.fc .fc-timegrid-col-frame {
  position: relative !important; /* Bắt buộc: làm gốc cho pseudo element */
}

.fc .fc-timegrid-col-frame::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 1px;
  height: 100%;
  background-color: #444;
  z-index: 15;
  pointer-events: none;
}

/* Xoá đường kẻ ở cột cuối */
.fc .fc-timegrid-cols > table > tbody > tr > td:last-child .fc-timegrid-col-frame::after {
  display: none;
}
#end-type option[value="noEnd"] {
  display: none;
}


`}</style>

      {/* Sidebar + Calendar */}
      <div className="w-full h-full" style={{ boxSizing: "border-box", overflow: "hidden" }}>
        <div style={{ display: "flex", width: "100%", height: "100%" }}>
          {/* LEFT: Filters */}
          <Flex
            className="left-sidebar"
            column
            gap="gap.large"
            styles={{
              width: "20%",
              overflowY: "auto",
              padding: "1rem",
              backgroundColor: "#121212",
              borderRight: "1px solid #333",
              boxSizing: "border-box"
            }}
          >
            {/* Tiêu đề */}
            <Text className="left-sidebar" content="Lịch Phòng Họp" weight="bold" size="large" />
            <Divider styles={{ root: { borderColor: "#333" } }} />

            {/* Chọn ngày */}
            <Flex column gap="gap.small">
              <Text className="left-sidebar" content="Chọn Ngày" weight="semibold" />
              <Input
                type="date"
                fluid
                value={DateTime.fromJSDate(selectedDate).toFormat("yyyy-LL-dd")}
                onChange={handleDateChange}
                styles={{
                  input: {
                    backgroundColor: "#1e1e1e",
                    color: "#fff",
                    borderColor: "#444",
                    borderRadius: "4px",
                    padding: "0.5rem"
                  }
                }}
              />
            </Flex>

            <Divider styles={{ root: { borderColor: "#333" } }} />

            {/* Phòng */}
            <Text className="left-sidebar" content="Phòng" weight="semibold" />

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {/* Tất cả phòng */}
              {/* <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  cursor: "pointer"
                }}
                onClick={() => setSelectedRooms([])}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    border: "2px solid #2979ff",
                    backgroundColor: selectedRooms.length === 0
                      ? "#2979ff"
                      : "transparent",
                    boxSizing: "border-box"
                  }}
                />
                <Text
                  content="Tất cả phòng"
                  styles={{ root: { color: selectedRooms.length === 0 ? "#fff" : "#aaa" } }}
                />
              </label> */}

              {/* Các phòng cụ thể */}
              {allRooms.map((room) => {
                // room: { emailAddress: string; displayName: string }
                const { emailAddress, displayName } = room;
                const color = roomColors[emailAddress] || "#00BFFF";
                const isSel = selectedRooms.includes(emailAddress);

                return (
                  <label
                    key={emailAddress}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      cursor: "pointer"
                    }}
                    onClick={() => handleRoomSelection(emailAddress)}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: "16px",
                        height: "16px",
                        borderRadius: "50%",
                        border: `2px solid ${color}`,
                        backgroundColor: isSel ? color : "transparent",
                        boxSizing: "border-box"
                      }}
                    />
                    <Text
                      content={displayName}
                      styles={{ root: { color: isSel ? "#fff" : "#aaa" } }}
                    />
                  </label>
                );
              })}
            </div>
            <Checkbox
              label={<span style={{ color: "#ffffff" }}>My Event</span>}
              checked={showMyEvents}
              onChange={(_, data) => setShowMyEvents(!!data?.checked)}
            />
          </Flex>
          <Divider styles={{ root: { borderColor: "#333" } }} />

          {/* RIGHT: Calendar */}
          <div style={{
            width: "80%",
            height: "calc(100vh )", // hoặc bất cứ chiều cao nào bạn muốn
            backgroundColor: "#1e1e1e",
            display: "flex",
            flexDirection: "column"
          }}>
            <div style={{ height: "100%", overflowY: "auto" }}>
              <FullCalendar
                height="100%"
                firstDay={1}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin, resourceTimeGridPlugin]}
                initialDate={selectedDate}
                ref={calendarRef}
                nowIndicator={true}
                resources={allRooms.map(r => ({
                  id: r.emailAddress,
                  title: r.displayName
                }))}
                scrollTime={DateTime.now().toFormat("HH:mm:ss")}
                initialView="timeGridWeek"
                stickyHeaderDates={true}
                events={convertScheduleToEvents(displaySchedule)}
                eventAllow={(dropInfo, draggedEvent) => {
                  return dropInfo.start! >= new Date() && dropInfo.end! > dropInfo.start!;
                }}
                selectAllow={(selectInfo) => {
                  return selectInfo.start >= new Date();
                }}
                eventDidMount={(info) => {
                  const el = info.el as HTMLElement;
                  // lấy màu gốc của event (chính là roomColors[item.room])
                  const baseColor = info.event.backgroundColor as string;
                  // 2) bo góc & padding
                  el.style.borderRadius = "6px";
                  el.style.padding = "0.15rem 0.5rem";
                  el.style.overflow = "hidden";
                  el.style.whiteSpace = "nowrap";
                  el.style.textOverflow = "ellipsis";
                  // 3) ensure relative để append thanh accent
                  el.style.position = "relative";
                  const bar = document.createElement("div");
                  Object.assign(bar.style, {
                    position: "absolute",
                    left: "2px",
                    top: "2px",
                    bottom: "2px",
                    width: "4px",
                    backgroundColor: baseColor,
                    borderRadius: "2px"
                  });
                  el.appendChild(bar);
                }}
                editable={true}
                droppable={true}
                navLinks={true}
                selectable={true}
                eventClick={handleEventClick}
                dateClick={handleDateClick}
                select={(info) => {
                  if (selectedEvent) {

                    setOpenEventDetails(false);
                    setSelectedEvent(null); // Reset selectedEvent
                  }
                  setFormData({ start: info.start, end: info.end, room: "", subject: "" }); // Clear subject when selecting a time
                  setBookingRoom("");
                  setOpenBookingForm(true);
                }}
                eventDrop={(info) => {

                  const eventOrganizer = info.event.extendedProps.organizer;
                  if (eventOrganizer !== userEmail) {

                    toast.error("You can only move your own events.");
                    info.revert();
                    return;
                  }

                  handleEventDrop(info);
                }}
                headerToolbar={{
                  left: "prev,next today",
                  center: "title",
                  right: "timeGridWeek,resourceTimeGridDay"
                }}
                buttonText={{
                  today: "Today",
                  timeGridWeek: "Week",
                  timeGridDay: "Day",
                  resourceTimeGridDay: "Day"
                }}

                datesSet={handleDatesSet}
                eventMouseEnter={(info) => {
                  const organizer = info.event.extendedProps.organizer || "Unknown";
                  const participants = info.event.extendedProps.participants || [];
                  const title = info.event.extendedProps.title || "Unknown";
                  const tooltipContent = `
                                      <div>
                                          <strong>Title:</strong> ${title}<br/>
                                          <strong>Organizer:</strong> ${organizer}<br/>
                                          <strong>Participants:</strong><br/>
                                          ${participants.map((p: string) => `- ${p}`).join("<br/>")}
                                      </div>
                                    `;
                  tippy(info.el, {
                    content: tooltipContent,
                    allowHTML: true,
                    placement: "top",
                    theme: "light-border"
                  });
                }}
              />
              {openBookingForm && !selectedEvent && (
                <>
                  <div className="custom-modal-overlay" onClick={() => setOpenBookingForm(false)} />
                  <div className="custom-modal">
                    <div className="custom-modal-header">
                      <h2>Sự kiện mới</h2>
                      <span onClick={() => setOpenBookingForm(false)} style={{ cursor: "pointer" }}>✕</span>
                    </div>

                    <div className="form-row">
                      <input
                        type="text"
                        placeholder="Thêm tiêu đề"
                        value={formData.subject}
                        onChange={e => setFormData({ ...formData, subject: e.currentTarget.value })}
                      />
                    </div>

                    <div className="form-row">
                      <input
                        id="att-search"
                        type="text"
                        placeholder="Nhập tên hoặc email"
                        value={searchQuery}
                        onChange={handleSearchChange}
                      />
                      {userSuggestions.length > 0 && (
                        <div className="suggestions-dropdown">
                          {userSuggestions.map(u => (
                            <div
                              key={u.mail}
                              className="suggestion-item"
                              onClick={() => { handleAddAttendee(u.mail); }}
                            >
                              {u.displayName} ({u.mail})
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="attendee-tokens">
                        {attendees.map(email => (
                          <span key={email} className="attendee-token">
                            {email}
                            <span className="remove-token" onClick={() => removeAttendee(email)}>×</span>
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="form-row" style={{ display: "flex", gap: "1rem" }}>
                      <input
                        type="date"
                        value={DateTime.fromJSDate(formData.start).toISODate()}
                        onChange={e => {
                          const d = DateTime.fromISO(e.currentTarget.value).toJSDate();
                          setFormData({ ...formData, start: d, end: d });
                        }}
                      />
                      {!allDay && (
                        <>
                          <input
                            type="time"
                            value={DateTime.fromJSDate(formData.start).toFormat("HH:mm")}
                            onChange={e => {
                              const [h, m] = e.currentTarget.value.split(":").map(Number);
                              setFormData({ ...formData, start: DateTime.fromJSDate(formData.start).set({ hour: h, minute: m }).toJSDate() });
                            }}
                          />
                          <input
                            type="time"
                            value={DateTime.fromJSDate(formData.end).toFormat("HH:mm")}
                            onChange={e => {
                              const [h, m] = e.currentTarget.value.split(":").map(Number);
                              setFormData({ ...formData, end: DateTime.fromJSDate(formData.end).set({ hour: h, minute: m }).toJSDate() });
                            }}
                          />
                        </>
                      )}
                      <label className="toggle-wrapper">
                        <input
                          type="checkbox"
                          checked={allDay}
                          onChange={e => setAllDay(e.currentTarget.checked)}
                        />
                        <span>Cả ngày</span>
                      </label>
                    </div>

                    {/* ————— Recurrence toggle button ————— */}
                    <div className="form-row">
                      <button
                        type="button"
                        className="recurrence-toggle"
                        onClick={() => setIsRecurring(prev => !prev)}
                      >
                        🔁 Đặt là định kỳ
                      </button>
                    </div>

                    {/* ————— Recurrence form (riêng biệt) ————— */}
                    {isRecurring && (
                      <div className="recurrence-form">
                        {/* 3.1: chọn interval & frequency */}
                        <div className="form-row">
                          <label>Lặp lại mỗi</label>
                          <input
                            type="number"
                            min={1}
                            value={recurrence.interval}
                            onChange={e => {
                              const newInterval = Number(e.currentTarget.value);
                              setRecurrence(prev => ({ ...prev, interval: newInterval }));
                            }}
                            style={{ width: 60, margin: "0 0.5rem" }}
                          />
                          <select
                            value={recurrence.frequency}
                            onChange={e => {
                              const freq = e.currentTarget.value as "daily" | "weekly" | "monthly";
                              setRecurrence(prev => ({ ...prev, frequency: freq, daysOfWeek: [] }));
                            }}
                          >
                            <option value="daily">ngày</option>
                            <option value="weekly">tuần</option>
                            <option value="monthly">tháng</option>
                          </select>
                        </div>

                        {/* 3.2: chọn ngày trong tuần nếu weekly */}
                        {recurrence.frequency === "weekly" && (
                          <div className="form-row weekdays-row">
                            {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map(day => (
                              <button
                                key={day}
                                type="button"
                                className={recurrence.daysOfWeek.includes(day) ? "day active" : "day"}
                                onClick={() =>
                                  setRecurrence(prev => ({
                                    ...prev,
                                    daysOfWeek: prev.daysOfWeek.includes(day)
                                      ? prev.daysOfWeek.filter(d => d !== day)
                                      : [...prev.daysOfWeek, day]
                                  }))
                                }
                              >
                                {day.slice(0, 2).toUpperCase()}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* ————— Chọn kiểu kết thúc ————— */}
                        <label htmlFor="end-type">Kết thúc</label>
                        <select
                          id="end-type"
                          value={recurrence.endType}
                          onChange={e => {
                            const t = e.currentTarget.value as "noEnd" | "endDate";
                            setRecurrence(prev => ({
                              ...prev,
                              endType: t,
                              endDate: t === "noEnd" ? null : prev.endDate // reset nếu noEnd
                            }));
                          }}
                        >
                          <option value="noEnd">Không bao giờ</option>
                          <option value="endDate">Ngày cụ thể</option>
                        </select>

                        {/* ————— Hiện datepicker khi chọn “Ngày cụ thể” ————— */}
                        {recurrence.endType === "endDate" && (
                          <input
                            type="date"
                            // nếu chưa có ngày, hiển thị ngày hôm nay làm mặc định
                            value={recurrence.endDate ?? DateTime.fromJSDate(formData.start).toISODate()}
                            onChange={e => {
                              const iso = e.currentTarget.value; // "2025-05-01"
                              setRecurrence(prev => ({
                                ...prev,
                                endDate: iso || null
                              }));
                            }}
                            // optional: không cho chọn trước ngày bắt đầu
                            min={DateTime.fromJSDate(formData.start).toISODate()}
                          />
                        )}

                      </div>
                    )}

                    <div className="form-row">
                      <label
                        htmlFor="room-select"
                        style={{ display: "block", marginBottom: "0.5rem", color: "#ccc" }}
                      >
                        Phòng:
                      </label>
                      <select
                        id="room-select"
                        value={formData.room}
                        onChange={e => {
                          const email = e.currentTarget.value;
                          console.log(email);
                          setBookingRoom(email);
                          setFormData(prev => ({
                            ...prev,
                            room: email
                          }));
                        }}
                        style={{
                          width: "100%",
                          background: "#2e2e2e",
                          color: bookingRoom ? "#fff" : "#888",
                          border: "1px solid #444",
                          borderRadius: "4px",
                          padding: "0.5rem",
                          fontSize: "0.9rem"
                        }}
                      >
                        <option value="" disabled>
                          -- Chọn phòng --
                        </option>
                        {allRooms.map(room => (
                          <option key={room.emailAddress} value={room.emailAddress}>
                            {room.displayName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="custom-modal-buttons">
                      <button className="btn-cancel" onClick={() => setOpenBookingForm(false)}>
                        Hủy
                      </button>
                      <button className="btn-save" onClick={() => handleCreateBooking(formData.start, formData.end)}>
                        Lưu
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Event Details Modal */}
              {openEventDetails && selectedEvent && (
                <>
                  <div className="custom-modal-overlay" onClick={() => setOpenEventDetails(false)} />
                  <div className="custom-modal">
                    <div className="custom-modal-header">
                      <h2>Chi tiết sự kiện</h2>
                      <span onClick={() => setOpenEventDetails(false)} style={{ cursor: "pointer" }}>✕</span>
                    </div>

                    <div className="form-row"><strong>Tiêu đề:</strong> {selectedEvent.title}</div>
                    <div className="form-row">
                      <strong>Bắt đầu:</strong> {DateTime.fromJSDate(selectedEvent.start).setZone("Asia/Ho_Chi_Minh").toFormat("dd/LL/yyyy HH:mm")}
                    </div>
                    <div className="form-row">
                      <strong>Kết thúc:</strong> {DateTime.fromJSDate(selectedEvent.end).setZone("Asia/Ho_Chi_Minh").toFormat("dd/LL/yyyy HH:mm")}
                    </div>
                    <div className="form-row"><strong>Người tạo:</strong> {selectedEvent.extendedProps.organizer}</div>

                    <div className="form-row">
                      <strong>Người tham dự:</strong><br />
                      {selectedEvent.extendedProps.participants.length > 0
                        ? (
                          <ul>
                            {selectedEvent.extendedProps.participants.map((p: string) => (
                              <li key={p}>{p}</li>
                            ))}
                          </ul>
                        )
                        : (
                          <em style={{ color: "#ccc" }}>Chưa có</em>
                        )}
                    </div>

                    <div className="custom-modal-buttons">
                      {selectedEvent.extendedProps.organizer === userEmail && (
                        <>
                          <button
                            className="btn-save"
                            onClick={() => {
                              setEditMode("single");
                              setFormData({ start: selectedEvent.start, end: selectedEvent.end, room: selectedEvent.room, subject: selectedEvent.title });
                              setBookingRoom(selectedEvent.extendedProps.room);
                              setAttendees(selectedEvent.extendedProps.participants);
                              setOpenBookingForm(true);
                              setOpenEventDetails(false);
                            }}
                          >
                            Chỉnh sửa
                          </button>
                          <button
                            className="btn-cancel"
                            onClick={promptDelete}
                          >
                            Xóa
                          </button>
                        </>
                      )}
                      <button className="btn-cancel" onClick={() => setOpenEventDetails(false)}>Đóng</button>
                    </div>
                  </div>
                </>
              )}

              {showDeleteConfirm && selectedEvent && (
                <>
                  <div className="custom-modal-overlay" onClick={() => setShowDeleteConfirm(false)} />
                  <div className="custom-modal">
                    <div className="custom-modal-header">
                      <h2>Xác nhận xóa</h2>
                      <span onClick={() => setShowDeleteConfirm(false)} style={{ cursor: "pointer" }}>✕</span>
                    </div>

                    <div className="form-row">
                      {selectedEvent.extendedProps.recurrence
                        ? "Sự kiện này là định kỳ. Bạn muốn xóa thế nào?"
                        : "Bạn có chắc muốn xóa sự kiện này không?"}
                    </div>

                    <div className="custom-modal-buttons">
                      {selectedEvent.extendedProps.recurrence && (
                        <>
                          <button
                            className="btn-save"
                            onClick={() => {
                              setSelectedEvent(selectedEvent.extendedProps.seriesMasterId);
                              handleDeleteEvent(false);
                              setSelectedEvent(null);
                              setShowDeleteConfirm(false);
                            }}
                          >
                            Chỉ xóa lần này
                          </button>
                          <button
                            className="btn-cancel"
                            onClick={() => {
                              handleDeleteEvent(true);
                              setShowDeleteConfirm(false);
                            }}
                          >
                            Xóa toàn bộ chuỗi
                          </button>
                        </>
                      )}
                      {!selectedEvent.extendedProps.recurrence && (
                        <button
                          className="btn-save"
                          onClick={() => {
                            handleDeleteEvent(false);
                            setShowDeleteConfirm(false);
                          }}
                        >
                          Xóa
                        </button>
                      )}
                      <button className="btn-cancel" onClick={() => setShowDeleteConfirm(false)}>Hủy</button>
                    </div>
                  </div>
                </>
              )}

              {openBookingForm && selectedEvent && (
                <>
                  {/* Overlay */}
                  <div
                    className="custom-modal-overlay"
                    onClick={() => {
                      setOpenBookingForm(false);
                      setSelectedEvent(null);
                    }}
                  />

                  {/* Modal Box */}
                  <div className="custom-modal">
                    <div className="custom-modal-header">
                      <h2>Chỉnh sửa sự kiện</h2>
                      <span
                        onClick={() => {
                          setOpenBookingForm(false);
                          setSelectedEvent(null);
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        ✕
                      </span>
                    </div>

                    {/* — Title — */}
                    <div className="form-row">
                      <input
                        type="text"
                        placeholder="Thêm tiêu đề"
                        value={formData.subject}
                        onChange={e => {
                          const newTitle = e.currentTarget.value; // Lấy giá trị ngay
                          setFormData(prev => ({
                            ...prev,
                            subject: newTitle // Dùng biến local, không đọc e bên trong updater
                          }));
                        }}
                      />
                    </div>

                    {/* — Attendees — */}
                    <div className="form-row">
                      <input
                        id="att-search"
                        type="text"
                        placeholder="Nhập tên hoặc email"
                        value={searchQuery}
                        onChange={handleSearchChange}
                      />
                      {userSuggestions.length > 0 && (
                        <div className="suggestions-dropdown">
                          {userSuggestions.map(u => (
                            <div
                              key={u.mail}
                              className="suggestion-item"
                              onClick={() => handleAddAttendee(u.mail)}
                            >
                              {u.displayName} ({u.mail})
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="attendee-tokens">
                        {attendees.map(email => (
                          <span key={email} className="attendee-token">
                            {email}
                            <span
                              className="remove-token"
                              onClick={() => removeAttendee(email)}
                            >
                              ×
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* — Date & Time — */}
                    <div className="form-row" style={{ display: "flex", gap: "1rem" }}>
                      <input
                        type="date"
                        value={DateTime.fromJSDate(formData.start).toISODate()}
                        onChange={e => {
                          const d = DateTime.fromISO(e.currentTarget.value).toJSDate();
                          setFormData(f => ({ ...f, start: d, end: d }));
                        }}
                      />
                      {!allDay && (
                        <>
                          <input
                            type="time"
                            value={DateTime.fromJSDate(formData.start).toFormat("HH:mm")}
                            onChange={e => {
                              const [h, m] = e.currentTarget.value.split(":").map(Number);
                              setFormData(f => ({
                                ...f,
                                start: DateTime.fromJSDate(f.start)
                                  .set({ hour: h, minute: m })
                                  .toJSDate()
                              }));
                            }}
                          />
                          <input
                            type="time"
                            value={DateTime.fromJSDate(formData.end).toFormat("HH:mm")}
                            onChange={e => {
                              const [h, m] = e.currentTarget.value.split(":").map(Number);
                              setFormData(f => ({
                                ...f,
                                end: DateTime.fromJSDate(f.end)
                                  .set({ hour: h, minute: m })
                                  .toJSDate()
                              }));
                            }}
                          />
                        </>
                      )}
                      <label className="toggle-wrapper">
                        <input
                          type="checkbox"
                          checked={allDay}
                          onChange={e => setAllDay(e.currentTarget.checked)}
                        />
                        <span>Cả ngày</span>
                      </label>
                    </div>

                    {/* — Recurrence Toggle — */}
                    <div className="form-row">
                      <button
                        type="button"
                        className="recurrence-toggle"
                        onClick={() => setIsRecurring(prev => !prev)}
                      >
                        🔁 Đặt là định kỳ
                      </button>
                    </div>

                    {/* — Recurrence Form — */}
                    {isRecurring && (
                      <div className="recurrence-form">
                        <label>Lặp lại mỗi</label>
                        <input
                          type="number"
                          min={1}
                          value={recurrence.interval}
                          onChange={e => {
                            const v = Number(e.currentTarget.value);
                            setRecurrence(prev => ({ ...prev, interval: v }));
                          }}
                        />
                        <select
                          value={recurrence.frequency}
                          onChange={e => {
                            const f = e.currentTarget.value as "daily" | "weekly" | "monthly";
                            setRecurrence(prev => ({
                              ...prev,
                              frequency: f,
                              daysOfWeek: []
                            }));
                          }}
                        >
                          <option value="daily">ngày</option>
                          <option value="weekly">tuần</option>
                          <option value="monthly">tháng</option>
                        </select>

                        {recurrence.frequency === "weekly" && (
                          <div className="weekdays-row">
                            {[
                              "monday",
                              "tuesday",
                              "wednesday",
                              "thursday",
                              "friday",
                              "saturday",
                              "sunday"
                            ].map(d => (
                              <button
                                key={d}
                                type="button"
                                className={recurrence.daysOfWeek.includes(d) ? "active" : ""}
                                onClick={() =>
                                  setRecurrence(prev => ({
                                    ...prev,
                                    daysOfWeek: prev.daysOfWeek.includes(d)
                                      ? prev.daysOfWeek.filter(x => x !== d)
                                      : [...prev.daysOfWeek, d]
                                  }))
                                }
                              >
                                {d.slice(0, 2).toUpperCase()}
                              </button>
                            ))}
                          </div>
                        )}

                        <label>Kết thúc</label>
                        <select
                          value={recurrence.endType}
                          onChange={e => {
                            const t = e.currentTarget.value as "noEnd" | "endDate";
                            setRecurrence(prev => ({
                              ...prev,
                              endType: t,
                              endDate: t === "noEnd" ? null : prev.endDate
                            }));
                          }}
                        >
                          <option value="noEnd">Không bao giờ</option>
                          <option value="endDate">Ngày cụ thể</option>
                        </select>
                        {recurrence.endType === "endDate" && (
                          <input
                            type="date"
                            value={recurrence.endDate ?? ""}
                            onChange={e => {
                              const iso = e.currentTarget.value; // lấy ngay giá trị date-only
                              setRecurrence(prev => ({
                                ...prev,
                                endDate: iso || null // truyền vào updater chỉ dùng biến local
                              }));
                            }}
                            min={DateTime.fromJSDate(formData.start).toISODate()}
                          />
                        )}
                      </div>
                    )}

                    {/* — Room Select — */}
                    <div className="form-row">
                      <label
                        htmlFor="room-select"
                        style={{ display: "block", marginBottom: 4, color: "#ccc" }}
                      >
                        Phòng:
                      </label>
                      <select
                        id="room-select"
                        value={bookingRoom}
                        onChange={e => {
                          const email = e.currentTarget.value;
                          setBookingRoom(email);
                        }}
                        style={{ width: "100%" }}
                      >
                        <option value="" disabled>
                          -- Chọn phòng --
                        </option>
                        {allRooms.map(room => (
                          <option key={room.emailAddress} value={room.emailAddress}>
                            {room.displayName}
                          </option>
                        ))}
                      </select>
                    </div>
                    {selectedEvent.extendedProps.recurrence && (
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button
                          className="btn-save"
                          onClick={() => {
                            setEditMode("single");
                            setFormData({ start: selectedEvent.start, end: selectedEvent.end, room: selectedEvent.room, subject: selectedEvent.title });
                            setBookingRoom(selectedEvent.extendedProps.room);
                            setAttendees(selectedEvent.extendedProps.participants);
                            setOpenBookingForm(true);
                            setOpenEventDetails(false);
                          }}
                        >
                          Chỉnh sửa lần này
                        </button>
                        <button
                          className="btn-save"
                          onClick={() => {
                            setEditMode("series");
                            setFormData({ start: selectedEvent.start, end: selectedEvent.end, room: selectedEvent.room, subject: selectedEvent.title });
                            setBookingRoom(selectedEvent.extendedProps.room);
                            setAttendees(selectedEvent.extendedProps.participants);
                            setOpenBookingForm(true);
                            setOpenEventDetails(false);
                          }}
                        >
                          Chỉnh sửa cả chuỗi
                        </button>
                      </div>
                    )}
                    {/* — Buttons — */}
                    <div className="custom-modal-buttons">
                      <button
                        className="btn-cancel"
                        onClick={() => {
                          setOpenBookingForm(false);
                          setSelectedEvent(null);
                        }}
                      >
                        Hủy
                      </button>
                      <button
                        className="btn-save"
                        onClick={() => editMode === "single"
                          ? handleUpdateEvent(selectedEvent.id) // như cũ
                          : handleUpdateSeries()}
                      >
                        Lưu thay đổi
                      </button>
                    </div>
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      </div >
    </>
  );
}
